// AssetsGeneration component - Handles generation of all publication assets
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BiCarousel, BiImageAlt } from "react-icons/bi";
import { GrDocumentVideo, GrDocumentPpt, GrDocumentImage, GrDocumentSound } from "react-icons/gr";
import { TbMessages } from "react-icons/tb";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ImFilePicture } from "react-icons/im";

import "./AssetsGeneration.css";
import { useAssetGeneration } from "./hooks/useAssetGeneration";
import AssetCard from "./ui/AssetCard";

// Import all dialogs
import AbstractVideoDialog from "../dialogs/AbstractVideoDialog";
import PresentationDialog from "../dialogs/PresentationDialog";
import PosterDialog from "../dialogs/PosterDialog";
import AudioSummaryDialog from "../dialogs/AudioSummaryDialog";
import AbstractVideoViewDialog from "../dialogs/AbstractVideoViewDialog";
import PresentationViewDialog from "../dialogs/PresentationViewDialog";
import PosterViewDialog from "../dialogs/PosterViewDialog";
import AudioSummaryViewDialog from "../dialogs/AudioSummaryViewDialog";
import CarouselViewDialog from "../dialogs/CarouselViewDialog";
import InstaPostViewDialog from "../dialogs/InstaPostViewDialogs";
import ImagesViewDialog from "../dialogs/ImagesViewDialog";
import InstaPostDialog from "../dialogs/InstaPostDialog";
import SlideEditorView from "../dialogs/SlideEditorView";
import VideoEditorView from "../dialogs/VideoEditorView";
import PreviousAssetsModal from "../dialogs/PreviousAssetsModal";


// Import all handlers
import * as videoHandlers from "./handlers/videoHandlers";
import * as presentationHandlers from "./handlers/presentationHandlers";
import * as posterHandlers from "./handlers/posterHandlers";
import * as audioHandlers from "./handlers/audioHandlers";
import * as imagesHandlers from "./handlers/imagesHandlers";
import * as carouselHandlers from "./handlers/carouselHandlers";
import * as instapostHandlers from "./handlers/instapostHandlers";

// Import API functions
import {
  fetchVideo,
  fetchPPT,
  fetchPoster,
  fetchAudio,
  fetchImages,
  fetchCarousel,
  fetchInstapost,
  downloadPoster,
  downloadInstapost,
  downloadImages,
} from "../../services/assetsGenerationAPI";

// State categories for transition detection
const ACTIVE_STATES = new Set(['queued', 'generating', 'merging']);
const STATIC_STATES = new Set(['TemplatesSelected', 'processing', 'completed', 'failed', 'error']);

const AssetsGeneration = ({ publicationData, refreshTrigger, setRefreshTrigger }) => {
  const navigate = useNavigate();
  const state = useAssetGeneration();
  // Track previous task statuses to detect active → static transitions
  const previousTaskStatuses = useRef(new Map());
  // Track which file assets have already been fetched (prevents duplicate fetches)
  const fetchedFileKeys = useRef(new Set());

  // Get user and organization data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");

  // Check if user is "demo" - if so, no restrictions apply
  const isDemoUser = userData.Username === "demo";

  // Get allowed and locked content types (only apply if not demo user)
  const allowedContentTypes = isDemoUser ? [] : (organizationData.allowed_content_types || []);
  const lockedContentTypes = isDemoUser ? [] : (organizationData.locked_content_types || []);

  // State for publication type
  // const [publicationType, setPublicationType] = useState("");

  // Previous Assets modal
  const [openPreviousAssets, setOpenPreviousAssets] = useState(false);
  const [previousAssetType, setPreviousAssetType] = useState(null);
  const [modalPptView, setModalPptView] = useState({ pptId: null, versionNumber: null, professionalType: null });
  // Track videoId+version selected from Previous Assets modal for AbstractVideoViewDialog
  const [videoViewVersion, setVideoViewVersion] = useState(null);
  const [videoAllVersions, setVideoAllVersions] = useState(null); // cross-project versions from PreviousAssetsModal
  const [globalVideoVersions, setGlobalVideoVersions] = useState(null); // track all versions to pass to the viewer directly from the card
  // Explicit pptId to pass to SlideEditorView — set synchronously so it isn't stale
  const [slideEditorPptId, setSlideEditorPptId] = useState(null);
  const [slideEditorProfessionalType, setSlideEditorProfessionalType] = useState(null);
  const [videoEditMode, setVideoEditMode] = useState(false);
  // Track absolute latest item from /versions
  const [latestVideoData, setLatestVideoData] = useState(null);
  const [latestPptData, setLatestPptData] = useState(null);
  const [latestPosterData, setLatestPosterData] = useState(null);
  // const [globalPosterVersions, setGlobalPosterVersions] = useState(null);
  const [latestAudioData, setLatestAudioData] = useState(null);
  // const [globalAudioVersions, setGlobalAudioVersions] = useState(null);
  const [publicationVersions, setPublicationVersions] = useState(null);


  // Store fetched raw DB data to prevent WS state overwriting
  const [fetchedPptGroup, setFetchedPptGroup] = useState(null);
  const [fetchedVideoGroup, setFetchedVideoGroup] = useState(null);
  const [fetchedPosterGroup, setFetchedPosterGroup] = useState(null);
  const [fetchedAudioGroup, setFetchedAudioGroup] = useState(null);
  const [videoProfessionalType, setVideoProfessionalType] = useState(null);
  // Sections pre-fetched when user clicks "Continue" on a queued project
  const [preloadedSections, setPreloadedSections] = useState([]);
  const [preloadedPptId, setPreloadedPptId] = useState(null);

  // Loading state for initial asset fetch
  const [isLoadingAssets, setIsLoadingAssets] = useState(true);

  // Allowed publication types for poster generation
  // const allowedPosterTypes = [
  //   "randomized clinical trial",
  //   "clinical trial",
  //   "research article",
  //   "original article",
  //   "systematic review & meta analysis",
  //   "review article",
  //   "review",
  //   "guidelines"
  // ];

  // Helper function to check if a content type is locked
  const isContentLocked = (contentType) => {
    if (isDemoUser) return false; // Demo user has no restrictions
    return lockedContentTypes.includes(contentType);
  };

  // Helper function to check if content type is not in allowed list (show as coming soon)
  const isContentNotAllowed = (contentType) => {
    if (isDemoUser) return false; // Demo user has access to everything
    if (allowedContentTypes.length === 0) return false; // If no restrictions set, allow all
    return !allowedContentTypes.includes(contentType);
  };

  const {
    publicationId,
    token,
    BASE_URL,
    alert,
    setAlert,
    activeTasks,
    taskStatus,
    rawTaskStatus,
    blocker,
    setupWebSocket,
    setActiveTasks,
    setTaskStatus,
    isGenerating,
    currentPublicationTaskStatus,
  } = state;

  // Warn user before closing/refreshing tab when generation is in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isGenerating) {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved. Asset generation is still in progress.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isGenerating]);

  // We now use the publicationData prop directly instead of making an API call.


  // Fetch existing assets on mount or when triggered
  useEffect(() => {
    const fetchExistingAssets = async () => {
      if (!publicationData) {
        return;
      }
      try {
        // Always reset all asset states when publicationId changes to prevent state leakage
        state.setPptUrl("");
        state.setPptId(null);
        state.setPptDownloadId(null);

        state.setAbstractVideoSrc("");
        // Only reset videoProjectId if no video dialog/editor is actively open.
        // When VideoEditorView is open and a merge completes, the refreshTrigger fires
        // this effect, but resetting videoProjectId to null here causes the "Play Video"
        // dialog to open without a project ID (it can't fetch the video from the backend).
        const isActivelyViewingVideoOnReset = state.openVideoViewDialog || state.openVideoEditorView || state.openAbstractVideoDialog;
        if (!isActivelyViewingVideoOnReset) {
          state.setVideoProjectId(null);
        }
        state.setVideoDownloadId(null);

        // Reset all Video-related states (only if no active task)
        // Note: Abstract video and PPT are reset above to prevent stale leakage
        if (!activeTasks.video) {
        }

        // Reset all Poster-related states (only if no active task)
        if (!activeTasks.poster) {
          state.setPosterUrl("");
          state.setPosterDownloadId(null);
        }

        // Reset all Audio-related states (only if no active task)
        if (!activeTasks.audio) {
          state.setAudioSummaryUrl("");
          state.setAudioDownloadId(null);
        }

        // Reset all Figures-related states (only if no active task)
        if (!activeTasks.images) {
          state.setAllFigures([]);
          state.setImageDownloadId(null);
        }

        // Reset all Carousel-related states (only if no active task)
        if (!activeTasks.carousel) {
          state.setCarouselUrl("");
          state.setCarouselDownloadId(null);
        }

        // Reset all Instapost-related states (only if no active task)
        if (!activeTasks.instapost) {
          state.setInstapostUrl("");
          state.setInstapostDownloadId(null);
        }

        // Removed corrupted manual taskStatus reset logic here.
        // useAssetGeneration.js already perfectly filters tasks by publicationId for this view.

        const versionsResponse = await fetch(`${BASE_URL}/publication/${publicationId}/versions`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const publication = publicationData;
        console.log('[DEBUG API] Found publication from props:', publication);
        console.log('[DEBUG API] Publication tasks:', publication?.task);

        if (!publication) {
          return;
        }

        // Store publication type for poster validation
        // if (publication.type) {
        //   setPublicationType(publication.type);
        // }

        // Process video file
        const videoFile = publication.UserFile?.find((f) => f.fileType === "video");
        // const videoIdFromFile = videoFile?.videoId || null;
        // --- Process Video via Versions API ---
        const versionsData = versionsResponse.ok ? await versionsResponse.json() : { versions: [] };
        setPublicationVersions(versionsData);
        const videoGroups = (versionsData.versions || []).filter((v) => v.type === "video");

        let latestVideo = null;
        let maxVideoTime = 0;
        let allVideoVersions = [];

        videoGroups.forEach(group => {
          (group.projects || []).forEach(p => {
            const t = new Date(p.updated_at || p.created_at || 0).getTime();
            if (t >= maxVideoTime) {
              maxVideoTime = t;
              latestVideo = { ...p };
            }
          });
          (group.versions || []).forEach(v => {
            allVideoVersions.push(v);
            const t = new Date(v.version_created_at || v.project_created_at || 0).getTime();
            if (t >= maxVideoTime) {
              maxVideoTime = t;
              latestVideo = { ...v, status: v.status || "completed" };
            }
          });
        });

        setLatestVideoData(latestVideo);
        setGlobalVideoVersions(allVideoVersions.sort((a, b) => new Date(a.version_created_at).getTime() - new Date(b.version_created_at).getTime()));

        // We still need fetchedVideoGroup for professionalType if we are viewing a specific video
        const latestVideoId = latestVideo ? latestVideo.project_id : null;
        const latestGroup = videoGroups.find(g =>
          (g.projects || []).some(p => p.project_id === latestVideoId) ||
          (g.versions || []).some(v => v.project_id === latestVideoId)
        ) || videoGroups[0] || null;
        setFetchedVideoGroup(latestGroup);

        if (latestVideo) {
          // Re-fetch video url if needed
          if (videoFile && !state.abstractVideoSrc && latestVideo.status === "completed") {
            fetchVideo(videoFile.id, token).then((url) => {
              state.setAbstractVideoSrc(url);
              state.setVideoDownloadId(videoFile.id);
            });
          }

          // Restore videoProjectId from the fetched data.
          // The rawTaskStatus sync effect (lines 662-676) is guarded by
          // isActivelyViewingVideo, so it won't restore the ID while the
          // VideoEditorView is open. We need to do it here directly.
          const latestVideoProjectId = latestVideo.project_id || latestVideo.id;
          if (latestVideoProjectId) {
            state.setVideoProjectId(latestVideoProjectId);
          }

          // Force sync active tasks and taskStatus if it is mid-flight
          if (latestVideo.status === "queued" || latestVideo.status === "generating") {
            const taskId = latestVideo.project_id || latestVideo.id || "legacy_video";
            setActiveTasks(prev => {
              const nextArr = [...(prev.video || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, video: nextArr };
            });
          }

          setTaskStatus(prev => {
            const taskId = latestVideo.project_id || latestVideo.id || "legacy_video";
            const prevVideo = prev.video || {};
            const liveProgress = prevVideo[taskId]?.progress || 0;
            return {
              ...prev,
              video: {
                ...prevVideo,
                [taskId]: {
                  ...(prevVideo[taskId] || {}),
                  progress: latestVideo.status === "completed" ? 100 : liveProgress,
                  status: latestVideo.status,
                  video_project_id: latestVideo.project_id || latestVideo.id,
                  publicationId: Number(publicationId),
                }
              }
            };
          });
        }

        // --- Process PPT via Versions API ---
        const pptGroups = (versionsData.versions || []).filter((v) => v.type === "ppt");

        let latestPpt = null;
        let maxPptTime = 0;

        pptGroups.forEach(group => {
          (group.projects || []).forEach(p => {
            const t = new Date(p.updated_at || p.created_at || 0).getTime();
            if (t >= maxPptTime) {
              maxPptTime = t;
              latestPpt = { ...p };
            }
          });
          (group.versions || []).forEach(v => {
            const t = new Date(v.version_created_at || v.project_created_at || 0).getTime();
            if (t >= maxPptTime) {
              maxPptTime = t;
              latestPpt = { ...v, status: v.status || "completed" };
            }
          });
        });

        setLatestPptData(latestPpt);

        const latestPptId = latestPpt ? latestPpt.project_id : null;
        const latestPptGroupObj = pptGroups.find(g =>
          (g.projects || []).some(p => p.project_id === latestPptId) ||
          (g.versions || []).some(v => v.project_id === latestPptId)
        ) || pptGroups[0] || null;

        setFetchedPptGroup(latestPptGroupObj);

        if (latestPpt) {
          if (latestPpt.status === "queued" || latestPpt.status === "generating") {
            const taskId = latestPpt.project_id || latestPpt.id || "legacy_ppt";
            setActiveTasks(prev => {
              const nextArr = [...(prev.ppt || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, ppt: nextArr };
            });
          }

          setTaskStatus(prev => {
            const taskId = latestPpt.project_id || latestPpt.id || "legacy_ppt";
            const prevPpt = prev.ppt || {};
            const liveProgress = prevPpt[taskId]?.progress || 0;
            return {
              ...prev,
              ppt: {
                ...prevPpt,
                [taskId]: {
                  ...(prevPpt[taskId] || {}),
                  progress: latestPpt.status === "completed" ? 100 : liveProgress,
                  status: latestPpt.status,
                  pptId: latestPpt.project_id || latestPpt.id,
                  publicationId: Number(publicationId),
                }
              }
            };
          });
        }
        // --- End Process PPT ---

        // --- Process Poster via Versions API ---
        const posterGroups = (versionsData.versions || []).filter((v) => v.type === "poster");

        let latestPoster = null;
        let maxPosterTime = 0;
        let allPosterVersions = [];

        posterGroups.forEach(group => {
          (group.projects || []).forEach(p => {
            const t = new Date(p.updated_at || p.created_at || 0).getTime();
            if (t >= maxPosterTime) {
              maxPosterTime = t;
              latestPoster = { ...p };
            }
          });
          (group.versions || []).forEach(v => {
            allPosterVersions.push(v);
            const t = new Date(v.version_created_at || v.project_created_at || 0).getTime();
            if (t >= maxPosterTime) {
              maxPosterTime = t;
              latestPoster = { ...v, status: v.status || "completed" };
            }
          });
        });

        setLatestPosterData(latestPoster);
        // setGlobalPosterVersions(allPosterVersions.sort((a, b) => new Date(a.version_created_at).getTime() - new Date(b.version_created_at).getTime()));

        const latestPosterId = latestPoster ? latestPoster.project_id : null;
        const latestPosterGroupObj = posterGroups.find(g =>
          (g.projects || []).some(p => p.project_id === latestPosterId) ||
          (g.versions || []).some(v => v.project_id === latestPosterId)
        ) || posterGroups[0] || null;
        setFetchedPosterGroup(latestPosterGroupObj);

        if (latestPoster) {
          if (!state.posterUrl && latestPoster.status === "completed") {
            const targetId = latestPoster.project_id || latestPoster.id;
            if (targetId) {
              fetchPoster(targetId, token).then((url) => {
                if (url && typeof url === "string" && !url.error) {
                  state.setPosterUrl(url);
                  state.setPosterDownloadId(targetId);
                }
              });
            }
          }

          if (latestPoster.status === "queued" || latestPoster.status === "generating") {
            const taskId = latestPoster.project_id || latestPoster.id || "legacy_poster";
            setActiveTasks(prev => {
              const nextArr = [...(prev.poster || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, poster: nextArr };
            });
          }

          setTaskStatus(prev => {
            const taskId = latestPoster.project_id || latestPoster.id || "legacy_poster";
            const prevPoster = prev.poster || {};
            const liveProgress = prevPoster[taskId]?.progress || 0;
            return {
              ...prev,
              poster: {
                ...prevPoster,
                [taskId]: {
                  ...(prevPoster[taskId] || {}),
                  progress: latestPoster.status === "completed" ? 100 : liveProgress,
                  status: latestPoster.status,
                  posterId: latestPoster.project_id || latestPoster.id,
                  publicationId: Number(publicationId),
                }
              }
            };
          });
        }
        // --- End Process Poster ---

        // --- Process Audio via Versions API ---
        const audioGroups = (versionsData.versions || []).filter((v) => v.type === "audio");

        let latestAudio = null;
        let maxAudioTime = 0;
        let allAudioVersions = [];

        audioGroups.forEach(group => {
          (group.projects || []).forEach(p => {
            const t = new Date(p.updated_at || p.created_at || 0).getTime();
            if (t >= maxAudioTime) {
              maxAudioTime = t;
              latestAudio = { ...p };
            }
          });
          (group.versions || []).forEach(v => {
            allAudioVersions.push(v);
            const t = new Date(v.version_created_at || v.project_created_at || 0).getTime();
            if (t >= maxAudioTime) {
              maxAudioTime = t;
              latestAudio = { ...v, status: v.status || "completed" };
            }
          });
        });

        setLatestAudioData(latestAudio);
        // setGlobalAudioVersions(allAudioVersions.sort((a, b) => new Date(a.version_created_at).getTime() - new Date(b.version_created_at).getTime()));

        const latestAudioId = latestAudio ? latestAudio.project_id : null;
        const latestAudioGroupObj = audioGroups.find(g =>
          (g.projects || []).some(p => p.project_id === latestAudioId) ||
          (g.versions || []).some(v => v.project_id === latestAudioId)
        ) || audioGroups[0] || null;
        setFetchedAudioGroup(latestAudioGroupObj);

        if (latestAudio) {
          if (!state.audioSummaryUrl && latestAudio.status === "completed") {
            const targetId = latestAudio.project_id || latestAudio.id;
            if (targetId) {
              fetchAudio(targetId, token).then((url) => {
                if (url && typeof url === "string" && !url.error) {
                  state.setAudioSummaryUrl(url);
                  state.setAudioDownloadId(targetId);
                }
              });
            }
          }

          if (latestAudio.status === "queued" || latestAudio.status === "generating") {
            const taskId = latestAudio.project_id || latestAudio.id || "legacy_audio";
            setActiveTasks(prev => {
              const nextArr = [...(prev.audio || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, audio: nextArr };
            });
          }

          setTaskStatus(prev => {
            const taskId = latestAudio.project_id || latestAudio.id || "legacy_audio";
            const prevAudio = prev.audio || {};
            const liveProgress = prevAudio[taskId]?.progress || 0;
            return {
              ...prev,
              audio: {
                ...prevAudio,
                [taskId]: {
                  ...(prevAudio[taskId] || {}),
                  progress: latestAudio.status === "completed" ? 100 : liveProgress,
                  status: latestAudio.status,
                  audioId: latestAudio.project_id || latestAudio.id,
                  publicationId: Number(publicationId),
                }
              }
            };
          });
        }
        // --- End Process Audio ---

        // Process carousel file
        const carouselFile = publication.UserFile.find((f) => f.fileType === "carousel");
        const carouselTask = publication.task?.find((t) => t.type === "carousel");
        if (carouselFile) {
          fetchCarousel(carouselFile.id, token).then((url) => {
            state.setCarouselUrl(url);
            state.setCarouselDownloadId(carouselFile.id);
          });
        }
        // Sync carousel task status if exists
        if (carouselTask) {
          const taskId = carouselTask?.job_id || carouselTask?.id || "legacy_carousel";
          setTaskStatus(prev => ({
            ...prev,
            carousel: {
              ...prev.carousel,
              [taskId]: {
                ...(prev.carousel?.[taskId] || {}),
                progress: carouselTask.status === "completed" ? 100 : (carouselTask.progress || 0),
                status: carouselTask.status,
                publicationId: Number(publicationId),
              }
            }
          }));
          // Set active task if in progress
          if (carouselTask.progress > 0 && carouselTask.progress < 100 && ["queued", "generating", "merging"].includes(carouselTask.status)) {
            const taskId = carouselTask?.job_id || carouselTask?.id || "legacy_carousel";
            setActiveTasks(prev => {
              const nextArr = [...(prev.carousel || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, carousel: nextArr };
            });
          }
        }

        // Process instapost file
        const instapostFile = publication.UserFile.find((f) => f.fileType === "post");
        const instapostTask = publication.task?.find((t) => t.type === "instapost");
        if (instapostFile) {
          fetchInstapost(instapostFile.id, token).then((url) => {
            state.setInstapostUrl(url);
            state.setInstapostDownloadId(instapostFile.id);
          });
        }
        // Sync instapost task status if exists
        if (instapostTask) {
          const taskId = instapostTask?.job_id || instapostTask?.id || "legacy_instapost";
          setTaskStatus(prev => ({
            ...prev,
            instapost: {
              ...prev.instapost,
              [taskId]: {
                ...(prev.instapost?.[taskId] || {}),
                progress: instapostTask.status === "completed" ? 100 : (instapostTask.progress || 0),
                status: instapostTask.status,
                publicationId: Number(publicationId),
              }
            }
          }));
          // Set active task if in progress
          if (instapostTask.progress > 0 && instapostTask.progress < 100 && ["queued", "generating", "merging"].includes(instapostTask.status)) {
            const taskId = instapostTask?.job_id || instapostTask?.id || "legacy_instapost";
            setActiveTasks(prev => {
              const nextArr = [...(prev.instapost || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, instapost: nextArr };
            });
          }
        }

        // Process images file
        const imagesFile = publication.UserFile.find((f) => f.fileType === "GraphImages");
        const imagesTask = publication.task?.find((t) => t.type === "images");
        if (imagesFile) {
          fetchImages(imagesFile.id, token)
            .then((urls) => {
              if (urls && Array.isArray(urls)) {
                state.setAllFigures(urls);
                state.setImageDownloadId(imagesFile.id);
              }
            })
            .catch((err) => console.error("Error fetching images:", err));
        }
        // Sync images task status if exists
        if (imagesTask) {
          const taskId = imagesTask?.job_id || imagesTask?.id || "legacy_images";
          setTaskStatus(prev => ({
            ...prev,
            images: {
              ...prev.images,
              [taskId]: {
                ...(prev.images?.[taskId] || {}),
                progress: imagesTask.status === "completed" ? 100 : (imagesTask.progress || 0),
                status: imagesTask.status,
                publicationId: Number(publicationId),
              }
            }
          }));
          // Set active task if in progress
          if (imagesTask.progress > 0 && imagesTask.progress < 100 && ["queued", "generating", "merging"].includes(imagesTask.status)) {
            const taskId = imagesTask?.job_id || imagesTask?.id || "legacy_images";
            setActiveTasks(prev => {
              const nextArr = [...(prev.images || [])];
              if (!nextArr.includes(taskId)) nextArr.push(taskId);
              return { ...prev, images: nextArr };
            });
          }
        }

        // Sync ppt_merge task status if exists (for restore slide editor progress overlay)
        const pptMergeTask = publication.task?.find((t) => t.type === "ppt_merge" && t.progress > 0 && t.progress <= 100);
        if (pptMergeTask) {
          const taskId = pptMergeTask.project_id || pptMergeTask.pptId || pptMergeTask.id || "legacy_merge";
          setTaskStatus(prev => ({
            ...prev,
            ppt_merge: {
              ...prev.ppt_merge,
              [taskId]: {
                progress: pptMergeTask.progress || 0,
                status: pptMergeTask.status,
                pptId: pptMergeTask.project_id || pptMergeTask.pptId || null,
                publicationId: Number(publicationId),
              }
            }
          }));
          if (pptMergeTask.progress > 0 && pptMergeTask.progress < 100 && ["queued", "generating", "merging"].includes(pptMergeTask.status)) {
            setActiveTasks(prev => {
              const currentMerge = prev.ppt_merge || [];
              if (!currentMerge.includes(taskId)) {
                return { ...prev, ppt_merge: [...currentMerge, taskId] };
              }
              return prev;
            });
          }
        }

        // Sync video_merge task status if exists (for restore video editor progress overlay)
        const videoMergeTask = publication.task?.find((t) => t.type === "video_merge" && t.progress > 0 && t.progress <= 100);
        if (videoMergeTask) {
          const taskId = videoMergeTask.project_id || videoMergeTask.video_project_id || videoMergeTask.id || "legacy_merge";
          setTaskStatus(prev => ({
            ...prev,
            video_merge: {
              ...prev.video_merge,
              [taskId]: {
                progress: videoMergeTask.progress || 0,
                status: videoMergeTask.status,
                video_project_id: videoMergeTask.project_id || videoMergeTask.video_project_id || null,
                publicationId: Number(publicationId),
              }
            }
          }));
          if (videoMergeTask.progress > 0 && videoMergeTask.progress < 100 && ["queued", "generating", "merging"].includes(videoMergeTask.status)) {
            setActiveTasks(prev => {
              const currentMerge = prev.video_merge || [];
              if (!currentMerge.includes(taskId)) {
                return { ...prev, video_merge: [...currentMerge, taskId] };
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error("Error fetching existing assets:", error);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    if (publicationId) {
      fetchExistingAssets();
    } else {
      setIsLoadingAssets(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationId, token, BASE_URL, publicationData, refreshTrigger]);

  // Handle WebSocket task state transitions
  // Only triggers refetch when a task moves from active (queued/generating/merging)
  // to static (TemplatesSelected/processing/completed/failed/error)
  useEffect(() => {
    let shouldRefresh = false;

    Object.entries(rawTaskStatus || {}).forEach(([taskType, taskDict]) => {
      // Special handling for ppt - sync pptId when available (filtered by publication)
      if (taskType === "ppt" || taskType === "ppt_merge") {
        Object.values(taskDict || {}).forEach(task => {
          if (task.publicationId && Number(task.publicationId) !== Number(publicationId)) return;

          const pptIdFromTask = task.pptId || task.ppt_id || task.projectId;
          if (pptIdFromTask && Number(pptIdFromTask) !== Number(state.pptId)) {
            const shouldUpdate = taskType === "ppt" || (taskType === "ppt_merge" && !rawTaskStatus.ppt?.pptId);
            if (shouldUpdate) {
              console.log(`[AssetsGeneration] Syncing ${taskType} ID to state:`, pptIdFromTask);
              state.setPptId(pptIdFromTask);
            }
          }
        });
      }

      // Special handling for video - sync videoProjectId (filtered by publication)
      if (taskType === "video" || taskType === "video_merge" || taskType === "video_regenerate") {
        Object.values(taskDict || {}).forEach(task => {
          if (task.publicationId && Number(task.publicationId) !== Number(publicationId)) return;

          const videoIdFromTask = task.video_project_id || task.videoProjectId || task.videoId;
          if (videoIdFromTask && Number(videoIdFromTask) !== Number(state.videoProjectId)) {
            const shouldUpdate = taskType === "video" || (taskType === "video_merge" && !rawTaskStatus.video?.video_project_id);
            const isActivelyViewingVideo = state.openVideoViewDialog || state.openVideoEditorView || state.openAbstractVideoDialog;
            if (shouldUpdate && !isActivelyViewingVideo) {
              console.log(`[AssetsGeneration] Syncing ${taskType} ID to state:`, videoIdFromTask);
              state.setVideoProjectId(videoIdFromTask);
            }
          }
        });
      }

      // ── Transition detection & file fetching (current publication only) ──
      Object.entries(taskDict || {}).forEach(([taskId, task]) => {
        // Only process tasks for the current publication
        if (task.publicationId && Number(task.publicationId) !== Number(publicationId)) return;

        const key = `${taskType}:${taskId}`;
        const prevStatus = previousTaskStatuses.current.get(key);
        const currentStatus = task.status;

        // Always update the tracked status
        previousTaskStatuses.current.set(key, currentStatus);

        // Skip if status hasn't actually changed
        if (prevStatus === currentStatus) return;

        // Detect genuine active → static transition
        if (ACTIVE_STATES.has(prevStatus) && STATIC_STATES.has(currentStatus)) {
          console.log(`[AssetsGeneration] Transition detected: ${taskType}:${taskId} ${prevStatus} → ${currentStatus}`);
          shouldRefresh = true;
        }

        // Handle file fetching for completed tasks (only once per unique file)
        if (currentStatus === "completed" && task.fileId) {
          const fileKey = `${taskType}:${taskId}:${task.fileId}`;
          if (!fetchedFileKeys.current.has(fileKey)) {
            fetchedFileKeys.current.add(fileKey);

            switch (taskType) {
              case "video":
              case "video_merge":
              case "video_regenerate":
                fetchVideo(task.fileId, token).then((url) => {
                  state.setAbstractVideoSrc(url);
                  state.setVideoDownloadId(task.fileId);
                  if (task.video_project_id || task.projectId) {
                    const isActivelyViewingVideo = state.openVideoViewDialog || state.openVideoEditorView || state.openAbstractVideoDialog;
                    if (!isActivelyViewingVideo) {
                      state.setVideoProjectId(task.video_project_id || task.projectId);
                    }
                  }
                });
                break;
              case "ppt":
              case "ppt_merge":
                fetchPPT(task.fileId, token).then((url) => {
                  state.setPptUrl(url);
                  state.setPptDownloadId(task.fileId);
                  const pptIdFromTask = task.pptId || task.ppt_id || task.ppt_project_id || task.projectId;
                  if (pptIdFromTask) {
                    const isActivelyViewingPpt = state.openPptViewDialog || state.openSlideEditorView || state.openPresentationDialog;
                    if (!isActivelyViewingPpt) {
                      state.setPptId(pptIdFromTask);
                    }
                  }
                });
                break;
              case "poster":
                fetchPoster(task.fileId, token).then((url) => {
                  state.setPosterUrl(url);
                  state.setPosterDownloadId(task.fileId);
                });
                break;
              case "audio":
                fetchAudio(task.fileId, token).then((url) => {
                  state.setAudioSummaryUrl(url);
                  state.setAudioDownloadId(task.fileId);
                });
                break;
              case "carousel":
                fetchCarousel(task.fileId, token).then((url) => {
                  state.setCarouselUrl(url);
                  state.setCarouselDownloadId(task.fileId);
                });
                break;
              case "post":
              case "instapost":
                fetchInstapost(task.fileId, token).then((url) => {
                  state.setInstapostUrl(url);
                  state.setInstapostDownloadId(task.fileId);
                });
                break;
              case "images":
                fetchImages(task.fileId, token).then((urls) => {
                  if (urls && Array.isArray(urls)) {
                    state.setAllFigures(urls);
                    state.setImageDownloadId(task.fileId);
                  }
                });
                break;
              default:
                break;
            }
          }
        }
      });
    });

    // Only trigger a single refetch when genuine transitions were detected
    if (shouldRefresh) {
      console.log('[AssetsGeneration] Active → Static transition detected, triggering refetch');
      setRefreshTrigger(prev => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTaskStatus, token, publicationId]);

  const handleCancelGeneration = async (projectId, generationType) => {
    if (!projectId) return;
    try {
      const response = await fetch(`${BASE_URL}/cancel-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_id: projectId,
          type: generationType
        })
      });

      if (response.status === 400) {
        const errorData = await response.json();
        setAlert({ severity: "warning", message: errorData.detail || "Cancellation is no longer possible." });
        return;
      }

      if (response.status === 404) {
        setAlert({ severity: "warning", message: "No active generation task found to cancel." });
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to cancel generation");
      }

      // Success
      setAlert({ severity: "success", message: `Successfully cancelled ${generationType} generation.` });
      
      // Manually remove from activeTasks so UI immediately drops progress bar
      setActiveTasks(prev => {
        const next = { ...prev };
        const typesToClear = [generationType, `${generationType}_merge`, `${generationType}_templates_llm`, `${generationType}_regenerate`];
        typesToClear.forEach(t => {
          if (next[t]) {
            next[t] = next[t].filter(id => String(id) !== String(projectId));
          }
        });
        return next;
      });

      // Trigger DB refetch
      if (setRefreshTrigger) setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error("Error cancelling task:", error);
      setAlert({ severity: "error", message: "Error cancelling task." });
    }
  };

  const handleConfirmNavigation = () => blocker?.proceed?.();
  const handleCancelNavigation = () => blocker?.reset?.() || blocker?.abort?.();

  // Helper to reliably check passive completions even after they leave activeTasks
  const isTaskLocallyCompleted = (taskType) => {
    // 1. Check WebSocket context state
    const inWs = Object.values(rawTaskStatus?.[taskType] || {}).some(
      (t) => Number(t.publicationId) === Number(publicationId) && (t.progress === 100 || t.status === "completed")
    );
    if (inWs) return true;

    // 2. Check local DB snapshot (prevents flicker before Context updates propagate)
    const dbTask = publicationData?.task?.find((t) => t.type === taskType);
    if (dbTask && (dbTask.progress === 100 || dbTask.status === "completed")) return true;

    // 3. For video and ppt, check the locally fetched versions API data
    if (taskType === "video" && latestVideoData?.status === "completed") return true;
    if (taskType === "ppt" && latestPptData?.status === "completed") return true;

    return false;
  };

  // ─── UI State Derivation Helper ──────────────
  const findTaskForId = (taskDict, targetId) => {
    if (!taskDict || !targetId) return null;
    for (const key in taskDict) {
      const t = taskDict[key];
      if (!t) continue;
      if (String(t.project_id) === String(targetId) || 
          String(t.id) === String(targetId) || 
          String(t.pptId) === String(targetId) || 
          String(t.video_project_id) === String(targetId) ||
          String(t.videoProjectId) === String(targetId) ||
          String(t.posterId) === String(targetId) ||
          String(t.audioId) === String(targetId) ||
          String(t.carouselId) === String(targetId) ||
          String(t.instapostId) === String(targetId) ||
          String(key) === String(targetId)) {
        return t;
      }
      
      const hasSpecificId = t.pptId || t.video_project_id || t.videoProjectId || t.posterId || t.audioId || t.carouselId || t.instapostId || t.project_id || t.id;
      if (!hasSpecificId && Number(t.publicationId) === Number(publicationId)) {
        return t;
      }
    }
    return null;
  };

  const getActiveTaskFromLatest = (assetType, latestData) => {
    if (!latestData) return null;
    const targetId = latestData.project_id || latestData.id;
    const displayStatus = latestData.status;

    const specificTask = findTaskForId(taskStatus[assetType], targetId) || findTaskForId(rawTaskStatus[assetType], targetId);
    const mergeTask = findTaskForId(taskStatus[`${assetType}_merge`], targetId) || findTaskForId(rawTaskStatus[`${assetType}_merge`], targetId);
    const templatesTaskKey = assetType === "ppt" ? "templates_llm" : `${assetType}_templates_llm`;
    const templateTask = findTaskForId(taskStatus[templatesTaskKey], targetId) || findTaskForId(rawTaskStatus[templatesTaskKey], targetId);

    if (displayStatus === "queued") {
      return specificTask || templateTask;
    } else if (displayStatus === "TemplatesSelected") {
      return templateTask || specificTask;
    } else if (displayStatus === "generating") {
      return specificTask || templateTask || mergeTask;
    } else if (displayStatus === "merging") {
      return mergeTask || specificTask;
    }
    return specificTask || mergeTask || templateTask;
  };

  // ─── UI State Derivation Helper ──────────────
  const normalizeStatus = (rawStatus) => {
    if (!rawStatus) return "";
    let normalized = rawStatus === "TemplatesSelected" ? "TemplatesSelected" : String(rawStatus).toLowerCase().replace(/[_\s]/g, "");
    if (normalized === "error" || normalized === "cancelled" || normalized === "terminated") normalized = "failed";
    if (normalized === "inprogress") normalized = "generating";
    return normalized;
  };

  const getFallbackStatus = (latestData, activeTask) => {
    const dbStatus = normalizeStatus(latestData?.status);
    if (!activeTask || !activeTask.status) return dbStatus;

    const wsStatus = normalizeStatus(activeTask.status);

    // If DB already says completed, ignore stale 'failed' websocket messages
    if (dbStatus === "completed" && ["failed", "error"].includes(wsStatus)) {
      return dbStatus;
    }
    
    // Otherwise, active task status takes precedence
    if (activeTask.status === "queued") return "queued";
    if (activeTask.status === "generating") return "generating";
    
    return wsStatus;
  };

  // ─── Video UI State Derivation ──────────────
  const activeVideoTask = getActiveTaskFromLatest("video", latestVideoData);
  let videoFallbackStatus = getFallbackStatus(latestVideoData, activeVideoTask);

  const isVideoQueued = videoFallbackStatus === "queued";
  const isVideoGenerating = videoFallbackStatus === "generating" && activeVideoTask?.progress >= 0 && activeVideoTask?.progress < 100;
  
  const isVideoFailedObj = !isVideoGenerating && !isVideoQueued && ["failed"].includes(videoFallbackStatus);
  const isVideoReviewContentObj = !isVideoGenerating && !isVideoQueued && ["processing", "merging"].includes(videoFallbackStatus);
  const isVideoTemplatesSelectedObj = !isVideoGenerating && !isVideoQueued && videoFallbackStatus === "TemplatesSelected";
  const isVideoCompletedObj = !isVideoGenerating && !isVideoQueued && videoFallbackStatus === "completed";
  const isVideoGeneratedObj = isVideoCompletedObj || isVideoReviewContentObj || isVideoTemplatesSelectedObj || isVideoFailedObj;

  const videoViewButtonTextObj = isVideoTemplatesSelectedObj
    ? "Resume"
    : (isVideoReviewContentObj ? "Review Content" : "Play Video");

  // ─── PPT UI State Derivation ──────────────
  const activePptTask = getActiveTaskFromLatest("ppt", latestPptData);
  let pptFallbackStatus = getFallbackStatus(latestPptData, activePptTask);

  console.log("[PPT DEBUG] latestPptData:", latestPptData?.project_id, "status:", latestPptData?.status);
  console.log("[PPT DEBUG] activePptTask:", activePptTask);
  console.log("[PPT DEBUG] pptFallbackStatus:", pptFallbackStatus);
  console.log("[PPT DEBUG] taskStatus.ppt:", taskStatus.ppt, "rawTaskStatus.ppt:", rawTaskStatus.ppt);

  const isPptQueued = pptFallbackStatus === "queued";
  const isPptGenerating = pptFallbackStatus === "generating" && activePptTask?.progress >= 0 && activePptTask?.progress < 100;

  const isPptFailedObj = !isPptGenerating && !isPptQueued && ["failed"].includes(pptFallbackStatus);
  const isPptReviewContentObj = !isPptGenerating && !isPptQueued && ["processing", "merging"].includes(pptFallbackStatus);
  const isPptTemplatesSelectedObj = !isPptGenerating && !isPptQueued && pptFallbackStatus === "TemplatesSelected";
  const isPptCompletedObj = !isPptGenerating && !isPptQueued && pptFallbackStatus === "completed";
  const isPptGeneratedObj = isPptCompletedObj || isPptReviewContentObj || isPptTemplatesSelectedObj || isPptFailedObj;

  console.log("[PPT DEBUG] isPptQueued:", isPptQueued, "isPptGenerating:", isPptGenerating, "isPptTemplatesSelectedObj:", isPptTemplatesSelectedObj, "isPptGeneratedObj:", isPptGeneratedObj);

  const pptViewButtonTextObj = isPptTemplatesSelectedObj
    ? "Resume"
    : (isPptReviewContentObj ? "Review Content" : "View PPT");

  // ─── Poster UI State Derivation ──────────────
  const activePosterTask = getActiveTaskFromLatest("poster", latestPosterData);
  let posterFallbackStatus = getFallbackStatus(latestPosterData, activePosterTask);

  const isPosterQueued = posterFallbackStatus === "queued";
  const isPosterGenerating = posterFallbackStatus === "generating" && activePosterTask?.progress >= 0 && activePosterTask?.progress < 100;

  const isPosterFailedObj = !isPosterGenerating && !isPosterQueued && ["failed"].includes(posterFallbackStatus);
  const isPosterCompletedObj = !isPosterGenerating && !isPosterQueued && posterFallbackStatus === "completed";
  const isPosterGeneratedObj = isPosterCompletedObj || isPosterFailedObj || !!state.posterUrl || isTaskLocallyCompleted("poster");

  // ─── Audio UI State Derivation ──────────────
  const activeAudioTask = getActiveTaskFromLatest("audio", latestAudioData);
  let audioFallbackStatus = getFallbackStatus(latestAudioData, activeAudioTask);

  const isAudioQueued = audioFallbackStatus === "queued";
  const isAudioGenerating = audioFallbackStatus === "generating" && activeAudioTask?.progress >= 0 && activeAudioTask?.progress < 100;

  const isAudioFailedObj = !isAudioGenerating && !isAudioQueued && ["failed"].includes(audioFallbackStatus);
  const isAudioCompletedObj = !isAudioGenerating && !isAudioQueued && audioFallbackStatus === "completed";
  const isAudioGeneratedObj = isAudioCompletedObj || isAudioFailedObj || !!state.audioSummaryUrl || isTaskLocallyCompleted("audio");


  return (
    <div className="right-section-container" style={{ height: "100%", overflowY: "auto" }}>
      {alert && (
        <Stack sx={{ position: "fixed", top: 70, right: 20, zIndex: 9999 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            sx={{ width: "300px" }}
          >
            {alert.message}
          </Alert>
        </Stack>
      )}

      {/* PubChat + Previous Assets Cards Row */}
      <div className="cards-container" style={{ marginBottom: "20px" }}>
        <div className="card">
          <TbMessages className="card-icon" />
          <div className="card-title">PubChat</div>
          <div className="card-description">
            Ask any question related to the publication and get concise, citation-linked answers, summaries, and follow-ups in seconds.
          </div>
          <button
            className="generate-button"
            onClick={() => navigate(`/pubChat/${publicationId}`)}
          >
            Start Chat
          </button>
        </div>

      </div>

      {/* Generate Assets Section */}
      <div className="generate-assets-title">Generate Assets</div>
      <div className="cards-container">
        {/* Abstract Video Card */}
        <AssetCard
          icon={GrDocumentVideo}
          title="Video"
          description="Quickly generate a short video summarizing your Scientific research publication."
          isLoading={!publicationData || isLoadingAssets}
          isGenerated={isVideoGeneratedObj}
          showDualButtons={true}
          isFailed={isVideoFailedObj}
          viewButtonText={videoViewButtonTextObj}
          isReviewContent={isVideoReviewContentObj}
          isGenerating={isVideoGenerating}
          progress={activeVideoTask?.progress || 0}
          isQueued={isVideoQueued}
          onCancel={() => handleCancelGeneration(activeVideoTask?.video_project_id || latestVideoData?.project_id, "video")}
          generateButtonText="Generate"
          showPreviousAssetsButton={true}
          onPreviousAssets={() => {
            setPreviousAssetType("video");
            setOpenPreviousAssets(true);
          }}
          onGenerate={() => {
            if (isVideoGenerating) {
              setAlert({ severity: "warning", message: "A video is already being generated. Please wait for it to complete." });
              return;
            }
            state.setVideoProjectId(null);
            videoHandlers.handleOpenAbstractVideoDialog(state.setOpenAbstractVideoDialog);
          }}
          onRegenerate={() => {
            state.setAbstractVideoSrc("");
            const videoIdToRegenerate = latestVideoData?.project_id;
            setTaskStatus(prev => {
              const next = { ...prev };
              if (next.video) {
                next.video = { ...next.video };
                delete next.video[videoIdToRegenerate];
              }
              if (next.video_merge) {
                next.video_merge = { ...next.video_merge };
                delete next.video_merge[videoIdToRegenerate];
              }
              return next;
            });
            videoHandlers.handleRegenerateVideo({
              videoId: videoIdToRegenerate,
              publicationId,
              token,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setRefreshTrigger,
            });
          }}
          onView={() => {
            const videoProjectIdToUse = latestVideoData?.project_id;

            if (isVideoTemplatesSelectedObj) {
              state.setVideoProjectId(videoProjectIdToUse);
              videoHandlers.handleOpenAbstractVideoDialog(state.setOpenAbstractVideoDialog);
            } else if (isVideoReviewContentObj) {
              state.setVideoProjectId(videoProjectIdToUse);
              setTaskStatus(prev => {
                const next = { ...prev };
                if (next.video_merge && next.video_merge[videoProjectIdToUse]?.progress === 100) {
                  next.video_merge = { ...next.video_merge };
                  delete next.video_merge[videoProjectIdToUse];
                }
                return next;
              });
              state.setOpenVideoEditorView(true);
            } else {
              state.setVideoProjectId(videoProjectIdToUse);
              setVideoAllVersions(globalVideoVersions);
              setVideoProfessionalType(fetchedVideoGroup?.professional_type);
              setVideoViewVersion(null);
              state.setOpenVideoViewDialog(true);
            }
          }}
          isLocked={isContentLocked("video")}
          comingSoon={isContentNotAllowed("video")}
        />

        {/* Presentation Card — always shows Generate */}
        <AssetCard
          icon={GrDocumentPpt}
          title="Presentation"
          description="Create a concise slide deck highlighting the key points of your Scientific research publication."
          isLoading={!publicationData || isLoadingAssets}
          isGenerated={isPptGeneratedObj}
          showDualButtons={true}
          isFailed={isPptFailedObj}
          viewButtonText={pptViewButtonTextObj}
          isReviewContent={isPptReviewContentObj}
          isGenerating={isPptGenerating}
          progress={activePptTask?.progress || 0}
          isQueued={isPptQueued}
          onCancel={() => handleCancelGeneration(activePptTask?.pptId || latestPptData?.project_id, "ppt")}
          generateButtonText="Generate"
          showPreviousAssetsButton={true}
          onPreviousAssets={() => {
            setPreviousAssetType("ppt");
            setOpenPreviousAssets(true);
          }}
          onGenerate={() => {
            if (isPptGenerating) {
              setAlert({ severity: "warning", message: "A presentation is already being generated. Please wait for it to complete." });
              return;
            }
            setPreloadedSections([]);
            setPreloadedPptId(null);
            presentationHandlers.handleOpenPresentationDialog(state.setOpenPresentationDialog);
          }}
          onRegenerate={() => {
            const activePptId = latestPptData?.project_id;
            state.setPptUrl("");
            setTaskStatus(prev => {
              const next = { ...prev };
              if (next.ppt) {
                next.ppt = { ...next.ppt };
                delete next.ppt[activePptId];
              }
              if (next.ppt_merge) {
                next.ppt_merge = { ...next.ppt_merge };
                delete next.ppt_merge[activePptId];
              }
              return next;
            });
            presentationHandlers.handleRegeneratePresentation({
              pptId: activePptId,
              publicationId,
              token,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setRefreshTrigger,
            });
          }}
          onView={() => {
            const activePptId = latestPptData?.project_id;
            if (isPptTemplatesSelectedObj) {
              fetch(`${BASE_URL}/get_ppt_templates?pptId=${activePptId}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
                .then(res => res.json())
                .then(sections => {
                  if (Array.isArray(sections) && sections.length > 0) {
                    setPreloadedSections(sections);
                    setPreloadedPptId(activePptId);
                    presentationHandlers.handleOpenPresentationDialog(state.setOpenPresentationDialog);
                  } else {
                    setPreloadedSections([]);
                    setPreloadedPptId(null);
                    presentationHandlers.handleOpenPresentationDialog(state.setOpenPresentationDialog);
                  }
                })
                .catch(err => {
                  console.error("Error fetching TemplatesSelected:", err);
                  presentationHandlers.handleOpenPresentationDialog(state.setOpenPresentationDialog);
                });
            } else if (isPptReviewContentObj) {
              setSlideEditorPptId(activePptId);
              setSlideEditorProfessionalType(fetchedPptGroup?.professional_type);
              state.setOpenSlideEditorView(true);
            } else {
              const storedVersion = sessionStorage.getItem(`pubvision_ppt_version_${activePptId}`);
              setModalPptView(prev => ({
                pptId: activePptId,
                versionNumber: storedVersion ? Number(storedVersion) : (prev.pptId === activePptId ? prev.versionNumber : null),
                professionalType: prev.pptId === activePptId ? prev.professionalType : null
              }));
              state.setOpenPptViewDialog(true);
            }
          }}
          isLocked={isContentLocked("ppt")}
          comingSoon={isContentNotAllowed("ppt")}
        />

        {/* Conference Poster Card */}
        <AssetCard
          icon={GrDocumentImage}
          title="Conference Poster"
          description="Design a professional research poster for your Scientific research conference presentation."
          isLoading={!publicationData || isLoadingAssets}
          showDualButtons={true}
          generateButtonText="Generate"
          isGenerated={isPosterGeneratedObj}
          isGenerating={isPosterGenerating}
          progress={activePosterTask?.progress || 0}
          isQueued={isPosterQueued}
          isFailed={isPosterFailedObj}
          showPreviousAssetsButton={true}
          onPreviousAssets={() => {
            setPreviousAssetType("poster");
            setOpenPreviousAssets(true);
          }}
          onCancel={() => handleCancelGeneration(activePosterTask?.posterId || latestPosterData?.project_id, "poster")}
          onRegenerate={() => {
            const activePosterId = latestPosterData?.project_id;
            state.setPosterUrl("");
            setTaskStatus(prev => {
              const next = { ...prev };
              if (next.poster) {
                next.poster = { ...next.poster };
                delete next.poster[activePosterId];
              }
              return next;
            });
            posterHandlers.handleStartGeneratingPoster({
              publicationId,
              token,
              posterContentLevel: fetchedPosterGroup?.professional_type,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setOpenPosterDialog: state.setOpenPosterDialog,
              posterId: activePosterId,
              setRefreshTrigger
            });
          }}
          onGenerate={() => posterHandlers.handleOpenPosterDialog(state.setOpenPosterDialog)}
          onView={async () => {
            if (state.posterUrl) {
              state.setOpenPosterViewDialog(true);
            } else {
              const targetId = latestPosterData?.project_id || latestPosterData?.id;
              if (targetId) {
                setAlert({ severity: "info", message: "Loading poster..." });
                try {
                  const url = await fetchPoster(targetId, token);
                  if (url && typeof url === "string" && !url.error) {
                    state.setPosterUrl(url);
                    state.setPosterDownloadId(targetId);
                    setAlert(null);
                    state.setOpenPosterViewDialog(true);
                  } else {
                    setAlert({ severity: "error", message: url?.error || "Failed to load poster file." });
                  }
                } catch (error) {
                  console.error("Error loading poster:", error);
                  setAlert({ severity: "error", message: "Failed to load poster" });
                }
              } else {
                setAlert({ severity: "error", message: "No poster available" });
              }
            }
          }}
          viewButtonText="View Poster"
          isLocked={isContentLocked("poster")}
          comingSoon={isContentNotAllowed("poster")}
        />

        {/* Audio Summary Card */}
        <AssetCard
          icon={GrDocumentSound}
          title="Audio Summary"
          description="Generate an audio narration summarizing the key findings of your Scientific research."
          isLoading={!publicationData || isLoadingAssets}
          showDualButtons={true}
          generateButtonText="Generate"
          isGenerated={isAudioGeneratedObj}
          isGenerating={isAudioGenerating}
          progress={activeAudioTask?.progress || 0}
          isQueued={isAudioQueued}
          isFailed={isAudioFailedObj}
          showPreviousAssetsButton={true}
          onPreviousAssets={() => {
            setPreviousAssetType("audio");
            setOpenPreviousAssets(true);
          }}
          onCancel={() => handleCancelGeneration(activeAudioTask?.audioId || latestAudioData?.project_id, "audio")}
          onRegenerate={() => {
            const activeAudioId = latestAudioData?.project_id;
            state.setAudioSummaryUrl("");
            setTaskStatus(prev => {
              const next = { ...prev };
              if (next.audio) {
                next.audio = { ...next.audio };
                delete next.audio[activeAudioId];
              }
              return next;
            });
            audioHandlers.handleStartGeneratingAudio({
              publicationId,
              token,
              audioLevel: fetchedAudioGroup?.professional_type,
              audioDuration: null,
              audioVoice: null,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setOpenAudioSummaryDialog: state.setOpenAudioSummaryDialog,
              audioId: activeAudioId,
              setRefreshTrigger
            });
          }}
          onGenerate={() => audioHandlers.handleOpenAudioSummaryDialog(state.setOpenAudioSummaryDialog)}
          onView={async () => {
            if (state.audioSummaryUrl) {
              state.setOpenAudioViewDialog(true);
            } else {
              const targetId = latestAudioData?.project_id || latestAudioData?.id;
              if (targetId) {
                setAlert({ severity: "info", message: "Loading audio..." });
                try {
                  const url = await fetchAudio(targetId, token);
                  if (url && typeof url === "string" && !url.error) {
                    state.setAudioSummaryUrl(url);
                    state.setAudioDownloadId(targetId);
                    setAlert(null);
                    state.setOpenAudioViewDialog(true);
                  } else {
                    setAlert({ severity: "error", message: url?.error || "Failed to load audio file." });
                  }
                } catch (error) {
                  console.error("Error loading audio:", error);
                  setAlert({ severity: "error", message: "Failed to load audio" });
                }
              } else {
                setAlert({ severity: "error", message: "No audio content available" });
              }
            }
          }}
          viewButtonText="Play Audio"
          isLocked={isContentLocked("audio")}
          comingSoon={isContentNotAllowed("audio")}
        />

        {/* Figures Card */}
        <AssetCard
          icon={ImFilePicture}
          title="Figures"
          description={
            <>
              Extract and organize all figures and images from your publication.
              <div style={{ marginTop: '8px', fontSize: '0.95em', fontStyle: 'italic', color: '#1e4de3' }}>
                Note: No credits required to extract
              </div>
            </>
          }
          isLoading={!publicationData || isLoadingAssets}
          isGenerated={
            state.allFigures.length > 0 || // Has figures
            isTaskLocallyCompleted("images") // Task reached 100%
          }
          isGenerating={activeTasks.images && taskStatus.images?.progress > 0 && taskStatus.images?.progress < 100}
          progress={taskStatus.images?.progress || 0}
          onGenerate={() =>
            imagesHandlers.handleStartGeneratingImages({
              publicationId,
              token,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setOpenImagesDialog: state.setOpenImagesDialog,
            })
          }
          generateButtonText="Extract"
          onView={async () => {
            console.log("[Figures onView] ========== VIEW FIGURES CLICKED ==========");
            console.log("[Figures onView] allFigures:", state.allFigures);
            console.log("[Figures onView] imageDownloadId:", state.imageDownloadId);
            console.log("[Figures onView] taskStatus.images:", taskStatus.images);

            if (state.allFigures.length > 0) {
              // Figures already available - open dialog directly
              console.log("[Figures onView] Figures exist, opening dialog");
              state.setOpenImagesDialog(true);
            } else {
              // Figures not available - need to fetch them
              console.log("[Figures onView] Figures array is EMPTY, fetching from backend...");
              try {
                console.log("[Figures onView] UserFile array:", publicationData?.UserFile);

                if (publicationData && publicationData.UserFile) {
                  const imagesFile = publicationData.UserFile.find((f) => f.fileType === "GraphImages");
                  console.log("[Figures onView] Images file found:", imagesFile);

                  if (imagesFile) {
                    setAlert({ severity: "info", message: "Loading figures..." });
                    console.log("[Figures onView] Fetching images with fileId:", imagesFile.id);
                    const urls = await fetchImages(imagesFile.id, token);
                    console.log("[Figures onView] Images fetched:", urls);
                    if (urls && Array.isArray(urls)) {
                      state.setAllFigures(urls);
                      state.setImageDownloadId(imagesFile.id);
                      setAlert(null);
                      state.setOpenImagesDialog(true);
                    } else {
                      console.error("[Figures onView] Invalid response format");
                      setAlert({ severity: "error", message: "Failed to load figures" });
                    }
                  } else {
                    console.error("[Figures onView] No images file found in UserFile array");
                    setAlert({ severity: "error", message: "Figures not found. Please wait a moment and try again." });
                  }
                } else {
                  console.error("[Figures onView] Publication or UserFile not found");
                  setAlert({ severity: "error", message: "No figures available" });
                }
              } catch (error) {
                console.error("[Figures onView] Error loading figures:", error);
                setAlert({ severity: "error", message: "Failed to load figures" });
              }
            }
          }}
          viewButtonText="View Figures"
          isLocked={isContentLocked("figures")}
          comingSoon={isContentNotAllowed("figures")}
        />
      </div>

      {/* Generate Social Media Formats Section */}
      <div className="generate-media-formats">Generate Social Media Formats</div>
      <div className="cards-container">
        {/* Carousel Card */}
        <AssetCard
          icon={BiCarousel}
          title="Carousel"
          description="Create an engaging social media carousel from your Scientific research highlights."
          isLoading={!publicationData || isLoadingAssets}
          isGenerated={
            state.carouselUrl || // Has carousel file
            isTaskLocallyCompleted("carousel") // Task reached 100%
          }
          isGenerating={activeTasks.carousel && taskStatus.carousel?.progress > 0 && taskStatus.carousel?.progress < 100}
          progress={taskStatus.carousel?.progress || 0}
          onGenerate={() =>
            carouselHandlers.handleStartGeneratingCarousel({
              publicationId,
              token,
              setActiveTasks,
              setTaskStatus,
              setAlert,
              setupWebSocket,
              setRefreshTrigger
            })
          }
          onView={async () => {
            if (state.carouselUrl) {
              state.setOpenCarouselViewDialog(true);
            } else {
              try {
                if (publicationData && publicationData.UserFile) {
                  const carouselFile = publicationData.UserFile.find((f) => f.fileType === "carousel");
                  if (carouselFile) {
                    setAlert({ severity: "info", message: "Loading carousel..." });
                    const url = await fetchCarousel(carouselFile.id, token);
                    state.setCarouselUrl(url);
                    state.setCarouselDownloadId(carouselFile.id);
                    setAlert(null);
                    state.setOpenCarouselViewDialog(true);
                  } else {
                    setAlert({ severity: "error", message: "Carousel file not found. Please wait a moment and try again." });
                  }
                } else {
                  setAlert({ severity: "error", message: "No carousel available" });
                }
              } catch (error) {
                console.error("Error loading carousel:", error);
                setAlert({ severity: "error", message: "Failed to load carousel" });
              }
            }
          }}
          viewButtonText="View Carousel"
          isLocked={isContentLocked("carousel")}
          comingSoon={isContentNotAllowed("carousels")}
        />

        {/* Instagram Post Card */}
        <AssetCard
          icon={BiImageAlt}
          title="Post"
          description="Generate a social media post to share your Scientific research findings."
          isLoading={!publicationData || isLoadingAssets}
          isGenerated={
            state.instapostUrl || // Has instapost file
            isTaskLocallyCompleted("instapost") || isTaskLocallyCompleted("post") // Task reached 100%
          }
          isGenerating={
            (activeTasks.instapost && taskStatus.instapost?.progress > 0 && taskStatus.instapost?.progress < 100) ||
            (activeTasks.post && taskStatus.post?.progress > 0 && taskStatus.post?.progress < 100)
          }
          progress={taskStatus.post?.progress || taskStatus.instapost?.progress || 0}
          onGenerate={() => instapostHandlers.handleOpenInstapostDialog(state.setOpenInstapostDialog)}
          onView={async () => {
            if (state.instapostUrl) {
              state.setOpenInstapostViewDialog(true);
            } else {
              try {
                if (publicationData && publicationData.UserFile) {
                  const instapostFile = publicationData.UserFile.find((f) => f.fileType === "post");
                  if (instapostFile) {
                    setAlert({ severity: "info", message: "Loading post..." });
                    const url = await fetchInstapost(instapostFile.id, token);
                    state.setInstapostUrl(url);
                    state.setInstapostDownloadId(instapostFile.id);
                    setAlert(null);
                    state.setOpenInstapostViewDialog(true);
                  } else {
                    setAlert({ severity: "error", message: "Post file not found. Please wait a moment and try again." });
                  }
                } else {
                  setAlert({ severity: "error", message: "No post available" });
                }
              } catch (error) {
                console.error("Error loading post:", error);
                setAlert({ severity: "error", message: "Failed to load post" });
              }
            }
          }}
          viewButtonText="View Post"
          isLocked={isContentLocked("post")}
          comingSoon={isContentNotAllowed("post")}
        />
      </div>

      {/* All Dialogs */}
      <AbstractVideoDialog
        open={state.openAbstractVideoDialog}
        onClose={(newVideoId, sectionsLoaded) => {
          videoHandlers.handleCloseAbstractVideoDialog(state.setOpenAbstractVideoDialog);

          const isLiveGenerationRunning = activeTasks.video && taskStatus.video?.progress > 0 && taskStatus.video?.progress < 100;
          if (newVideoId && !isLiveGenerationRunning) {
            console.log("[AbstractVideoDialog onClose] Re-fetching true latest state after dialog closed");
            setRefreshTrigger(prev => prev + 1);
          }
        }}
        videoDuration={state.videoDuration}
        setVideoDuration={state.setVideoDuration}
        language={state.language}
        setLanguage={state.setLanguage}
        selectedColor={state.selectedColor}
        setSelectedColor={state.setSelectedColor}
        customColor={state.customColor}
        setCustomColor={state.setCustomColor}
        contentLevel={state.videoContentLevel}
        setContentLevel={state.setVideoContentLevel}
        publicationId={publicationId}
        videoId={state.videoProjectId}
        selectedSections={state.videoSelectedSections}
        setSelectedSections={state.setVideoSelectedSections}
        setActiveTasks={setActiveTasks}
        setTaskStatus={setTaskStatus}
        setRefreshTrigger={setRefreshTrigger}
        onStartGenerating={(selectedMusic, videoId) => {
          // Clear stale video file states for a fresh generation run
          // (mirrors what PPT does in onStartGeneratingPresentation)
          // Clear stale video file states for a fresh generation run
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.video;
            delete next.video_merge;
            return next;
          });

          videoHandlers.handleStartGenerating({
            publicationId,
            token,
            contentLevel: state.videoContentLevel,
            selectedSections: state.videoSelectedSections,
            selectedMusic,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setRefreshTrigger,
            setOpenAbstractVideoDialog: state.setOpenAbstractVideoDialog,
            setVideoProjectId: state.setVideoProjectId,
            existingVideoProjectId: videoId,
          });
        }}
      />

      <PresentationDialog
        open={state.openPresentationDialog}
        onClose={(newPptId, sectionsLoaded) => {
          presentationHandlers.handleClosePresentationDialog(state.setOpenPresentationDialog);
          // If a live generation is in progress, do NOT overwrite its state.
          // The user simply browsed/cancelled a resumed previous asset — the active
          // generation's progress bar must remain visible.
          const isLiveGenerationRunning = activeTasks.ppt && taskStatus.ppt?.progress > 0 && taskStatus.ppt?.progress < 100;
          if (newPptId && !isLiveGenerationRunning) {
            console.log("[PresentationDialog onClose] Re-fetching true latest state after dialog closed");
            setRefreshTrigger(prev => prev + 1);
          } else if (newPptId && isLiveGenerationRunning) {
            console.log("[PresentationDialog onClose] Skipping state sync — live generation in progress for pptId:", taskStatus.ppt?.pptId);
          }
        }}
        slidesCount={state.slidesCount}
        setSlidesCount={state.setSlidesCount}
        presentationLanguage={state.presentationLanguage}
        setPresentationLanguage={state.setPresentationLanguage}
        speakerNotes={state.speakerNotes}
        setSpeakerNotes={state.setSpeakerNotes}
        contentLevel={state.contentLevel}
        setContentLevel={state.setContentLevel}
        presentationSelectedColor={state.presentationSelectedColor}
        setPresentationSelectedColor={state.setPresentationSelectedColor}
        presentationCustomColor={state.presentationCustomColor}
        setPresentationCustomColor={state.setPresentationCustomColor}
        publicationId={publicationId}
        selectedSections={state.selectedSections}
        setSelectedSections={state.setSelectedSections}
        preloadedSections={preloadedSections}
        preloadedPptId={preloadedPptId}
        setActiveTasks={setActiveTasks}
        setTaskStatus={setTaskStatus}
        setRefreshTrigger={setRefreshTrigger}
        onStartGeneratingPresentation={(pptId) => {
          // Guard: block if a generation is already in progress
          if (activeTasks.ppt && taskStatus.ppt?.progress > 0 && taskStatus.ppt?.progress < 100) {
            setAlert({ severity: "warning", message: "A presentation is already being generated. Please wait for it to complete." });
            presentationHandlers.handleClosePresentationDialog(state.setOpenPresentationDialog);
            return;
          }
          // Clear preloaded data after dialog picks it up
          setPreloadedSections([]);
          setPreloadedPptId(null);
          // Clear stale presentation file states for a fresh/resumed generation run
          // Clear stale presentation file states for a fresh/resumed generation run
          state.setPptUrl("");
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.ppt;
            delete next.ppt_merge;
            return next;
          });
          presentationHandlers.handleStartGeneratingPresentation({
            publicationId,
            token,
            contentLevel: state.contentLevel,
            slidesCount: state.slidesCount,
            selectedSections: state.selectedSections,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setRefreshTrigger,
            setOpenPresentationDialog: state.setOpenPresentationDialog,
            setPptId: state.setPptId,
            existingPptId: pptId || latestPptData?.project_id,
          });
        }}
      />

      {/* Slide Editor View */}
      <SlideEditorView
        open={state.openSlideEditorView}
        onClose={() => {
          state.setOpenSlideEditorView(false);
          setSlideEditorPptId(null); // reset so next open uses activePptId
          setSlideEditorProfessionalType(null); // reset
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.ppt_merge;
            return next;
          });
          setRefreshTrigger(prev => prev + 1); // Refresh assets in case they merged
        }}
        pptId={slideEditorPptId || latestPptData?.project_id}
        professionalType={slideEditorProfessionalType || fetchedPptGroup?.professional_type}
        token={token}
        publicationId={publicationId}
        isAlreadyMerged={state.hasPptFile && !isPptReviewContentObj && !slideEditorPptId}
        initialPptUrl={state.pptUrl}
        onPptCreated={(pptDownloadUrl, projectId) => {
          // Update main-view state optimistically if it's the active project
          if (!slideEditorPptId || slideEditorPptId === latestPptData?.project_id) {
            state.setPptUrl(pptDownloadUrl);
            if (projectId) {
              state.setPptDownloadId(projectId);
            }
          }
        }}
      />

      {/* PresentationViewDialog — opened from Previous Assets modal "View PPT" */}
      <PresentationViewDialog
        open={state.openPptViewDialog}
        onClose={() => {
          state.setOpenPptViewDialog(false);
          // Preserve modalPptView state so dashboard "View PPT" remembers the selection
        }}
        pptId={modalPptView.pptId || latestPptData?.project_id}
        initialVersion={modalPptView.versionNumber}
        versionsList={modalPptView.allVersions}
        professionalType={modalPptView.professionalType || fetchedPptGroup?.professional_type}
        publicationId={publicationId}
        token={token}
        pptUrl={state.pptUrl}
        setAlert={setAlert}
        onEdit={(editPptId) => {
          state.setOpenPptViewDialog(false);
          setSlideEditorPptId(editPptId || modalPptView.pptId || latestPptData?.project_id);
          setSlideEditorProfessionalType(modalPptView.professionalType || fetchedPptGroup?.professional_type);
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.ppt_merge;
            return next;
          });
          state.setOpenSlideEditorView(true);
        }}
      />

      {/* Video Editor View */}
      <VideoEditorView
        open={state.openVideoEditorView}
        onClose={() => {
          state.setOpenVideoEditorView(false);
          setVideoEditMode(false);
          setTaskStatus(prev => {
            const next = { ...prev };
            if (next.video_merge) {
              next.video_merge = { ...next.video_merge };
              Object.keys(next.video_merge).forEach(key => {
                if (next.video_merge[key].progress === 100) {
                  delete next.video_merge[key];
                }
              });
            }
            return next;
          });
          setRefreshTrigger(prev => prev + 1); // Unconditionally refresh assets on close as requested
        }}
        videoProjectId={state.videoProjectId}
        token={token}
        publicationId={publicationId}
        professionalType={videoProfessionalType || fetchedVideoGroup?.professional_type}
        isAlreadyMerged={!!state.abstractVideoUrl && !videoEditMode}
        onVideoCreated={() => {
          // We intentionally do NOT trigger a refresh here.
          // Triggering a refresh aggressively wipes `state.videoProjectId`
          // before the user can click "Play Video". The dashboard will 
          // naturally refresh when the user closes this dialog.
        }}
      />

      <PosterDialog
        open={state.openPosterDialog}
        onClose={() => posterHandlers.handleClosePosterDialog(state.setOpenPosterDialog)}
        posterSections={state.posterSections}
        setPosterSections={state.setPosterSections}
        posterLanguage={state.posterLanguage}
        setPosterLanguage={state.setPosterLanguage}
        addReferences={state.addReferences}
        setAddReferences={state.setAddReferences}
        posterContentLevel={state.posterContentLevel}
        setPosterContentLevel={state.setPosterContentLevel}
        posterSelectedColor={state.posterSelectedColor}
        setPosterSelectedColor={state.setPosterSelectedColor}
        posterCustomColor={state.posterCustomColor}
        setPosterCustomColor={state.setPosterCustomColor}
        onStartGeneratingPoster={() =>
          posterHandlers.handleStartGeneratingPoster({
            publicationId,
            token,
            posterContentLevel: state.posterContentLevel,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setOpenPosterDialog: state.setOpenPosterDialog,
            setRefreshTrigger
          })
        }
      />

      <AudioSummaryDialog
        open={state.openAudioSummaryDialog}
        onClose={() => audioHandlers.handleCloseAudioSummaryDialog(state.setOpenAudioSummaryDialog)}
        audioDuration={state.audioDuration}
        setAudioDuration={state.setAudioDuration}
        audioLanguage={state.audioLanguage}
        setAudioLanguage={state.setAudioLanguage}
        audioVoice={state.audioVoice}
        setAudioVoice={state.setAudioVoice}
        audioLevel={state.audioLevel}
        setAudioLevel={state.setAudioLevel}
        onStartGeneratingAudio={() =>
          audioHandlers.handleStartGeneratingAudio({
            publicationId,
            token,
            audioLevel: state.audioLevel,
            audioDuration: state.audioDuration,
            audioVoice: state.audioVoice,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setOpenAudioSummaryDialog: state.setOpenAudioSummaryDialog,
            setRefreshTrigger
          })
        }
      />

      <InstaPostDialog
        open={state.openInstapostDialog}
        onClose={() => instapostHandlers.handleCloseInstapostDialog(state.setOpenInstapostDialog)}
        postSection={state.postSection}
        setPostSection={state.setPostSection}
        postContentLevel={state.postContentLevel}
        setPostContentLevel={state.setPostContentLevel}
        postSelectedColor={state.postSelectedColor}
        setPostSelectedColor={state.setPostSelectedColor}
        postCustomColor={state.postCustomColor}
        setPostCustomColor={state.setPostCustomColor}
        onStartGeneratingPost={() =>
          instapostHandlers.handleStartGeneratingInstapost({
            publicationId,
            token,
            postSection: state.postSection,
            postContentLevel: state.postContentLevel,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setOpenInstapostDialog: state.setOpenInstapostDialog,
            setRefreshTrigger
          })
        }
      />

      {/* View Dialogs */}
      <AbstractVideoViewDialog
        open={state.openVideoViewDialog}
        onClose={() => {
          state.setOpenVideoViewDialog(false);
          setVideoViewVersion(null);
          setVideoAllVersions(null);
        }}
        videoProjectId={state.videoProjectId}
        initialVersion={videoViewVersion}
        allVersions={videoAllVersions}
        professionalType={videoProfessionalType || fetchedVideoGroup?.professional_type}
        onEdit={(currentVideoId, currentVersionNumber) => {
          state.setOpenVideoViewDialog(false);
          setVideoEditMode(true);
          if (currentVideoId) {
            state.setVideoProjectId(currentVideoId);
          }
          setTaskStatus(prev => {
            const next = { ...prev };
            if (next.video_merge && currentVideoId && next.video_merge[currentVideoId]?.progress === 100) {
              next.video_merge = { ...next.video_merge };
              delete next.video_merge[currentVideoId];
            }
            return next;
          });
          state.setOpenVideoEditorView(true);
        }}
      />

      <PosterViewDialog
        open={state.openPosterViewDialog}
        onClose={() => state.setOpenPosterViewDialog(false)}
        posterUrl={state.posterUrl}
        posterId={state.posterDownloadId}
        onDownload={() => {
          if (state.posterDownloadId) {
            downloadPoster(state.posterDownloadId, token);
          }
        }}
      />

      <AudioSummaryViewDialog
        open={state.openAudioViewDialog}
        onClose={() => state.setOpenAudioViewDialog(false)}
        audioSrc={state.audioSummaryUrl}
        audioDownloadId={state.audioDownloadId}
        token={token}
        setAlert={setAlert}
      />

      <ImagesViewDialog
        open={state.openImagesDialog}
        onClose={() => state.setOpenImagesDialog(false)}
        images={state.allFigures}
        onDownload={() => {
          if (state.imageDownloadId) {
            downloadImages(state.imageDownloadId, token);
          }
        }}
      />

      <CarouselViewDialog
        open={state.openCarouselViewDialog}
        onClose={() => state.setOpenCarouselViewDialog(false)}
        carouselUrl={state.carouselUrl}
      />

      <InstaPostViewDialog
        open={state.openInstapostViewDialog}
        onClose={() => state.setOpenInstapostViewDialog(false)}
        instaUrl={state.instapostUrl}
        instaPostId={state.instapostDownloadId}
        onDownload={() => {
          if (state.instapostDownloadId) {
            downloadInstapost(state.instapostDownloadId, token);
          }
        }}
      />

      {/* Previous Assets Modal */}
      <PreviousAssetsModal
        open={openPreviousAssets}
        onClose={() => setOpenPreviousAssets(false)}
        assetType={previousAssetType}
        publicationId={publicationId}
        token={token}
        publicationVersions={publicationVersions}
        isLoadingAssets={isLoadingAssets}
        taskStatus={taskStatus}
        rawTaskStatus={rawTaskStatus}
        currentPublicationTaskStatus={currentPublicationTaskStatus}
        onReviewContent={(pptId, professionalType) => {
          setOpenPreviousAssets(false);
          setSlideEditorPptId(pptId); // set synchronously — no stale-state risk
          setSlideEditorProfessionalType(professionalType);
          state.setPptId(pptId);
          state.setOpenSlideEditorView(true);
        }}
        onViewPpt={(pptId, versionNumber, allVersions, professionalType) => {
          setOpenPreviousAssets(false);
          setModalPptView({ pptId, versionNumber, allVersions, professionalType });
          state.setPptId(pptId);
          state.setOpenPptViewDialog(true);
        }}
        onRegenerate={(pptId) => {
          // Guard: block if a generation is already in progress
          if (activeTasks.ppt && taskStatus.ppt?.progress > 0 && taskStatus.ppt?.progress < 100) {
            setAlert({ severity: "warning", message: "A presentation is already being generated. Please wait for it to complete." });
            setOpenPreviousAssets(false);
            return;
          }
          setOpenPreviousAssets(false);
          // Clear stale presentation file states before starting regeneration from history
          // Clear stale presentation file states before starting regeneration from history
          state.setPptUrl("");
          state.setPptId(pptId);
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.ppt;
            delete next.ppt_merge;
            return next;
          });
          presentationHandlers.handleRegeneratePresentation({
            pptId,
            publicationId,
            token,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
          });
        }}
        onResume={async (pptId) => {
          setOpenPreviousAssets(false);
          // If a generation is already running, warn the user but still let
          // them browse the sections (the Start Generating guard will block duplicates).
          if (activeTasks.ppt && taskStatus.ppt?.progress > 0 && taskStatus.ppt?.progress < 100) {
            setAlert({ severity: "info", message: "A generation is currently in progress. You can browse sections but cannot start another generation until it completes." });
          }
          try {
            const res = await fetch(
              `${process.env.REACT_APP_BASE_URL}/get_ppt_templates?pptId=${pptId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error("Failed to fetch sections");
            const sections = await res.json();
            if (Array.isArray(sections) && sections.length > 0) {
              setPreloadedSections(sections);
              setPreloadedPptId(pptId);
              state.setOpenPresentationDialog(true);
            } else {
              // No sections stored yet — open fresh wizard
              state.setOpenPresentationDialog(true);
            }
          } catch (err) {
            console.error("[onResume] Failed to fetch sections:", err);
            // Fallback: open fresh wizard
            state.setOpenPresentationDialog(true);
          }
        }}
        onReviewContentVideo={(videoId, professionalType) => {
          setOpenPreviousAssets(false);
          state.setVideoProjectId(videoId);
          setVideoProfessionalType(professionalType);
          setTaskStatus(prev => {
            const next = { ...prev };
            if (next.video_merge && next.video_merge[videoId]?.progress === 100) {
              next.video_merge = { ...next.video_merge };
              delete next.video_merge[videoId];
            }
            return next;
          });
          state.setOpenVideoEditorView(true);
        }}
        onViewVideo={(videoId, versionNumber, allVersions, selectedDisplayVersion, professionalType) => {
          setOpenPreviousAssets(false);
          state.setVideoProjectId(videoId);
          setVideoViewVersion(selectedDisplayVersion || versionNumber);
          setVideoAllVersions(allVersions || null);
          setVideoProfessionalType(professionalType);
          state.setOpenVideoViewDialog(true);
        }}
        onRegenerateVideo={(videoId) => {
          if (activeTasks.video && taskStatus.video?.progress > 0 && taskStatus.video?.progress < 100) {
            setAlert({ severity: "warning", message: "A video is already being generated. Please wait for it to complete." });
            setOpenPreviousAssets(false);
            return;
          }
          setOpenPreviousAssets(false);
          state.setAbstractVideoSrc("");
          state.setVideoProjectId(videoId);
          setTaskStatus(prev => {
            const next = { ...prev };
            delete next.video;
            delete next.video_merge;
            return next;
          });
          videoHandlers.handleRegenerateVideo({
            videoId,
            publicationId,
            token,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
          });
        }}
        onResumeVideo={(videoId) => {
          setOpenPreviousAssets(false);
          if (activeTasks.video && taskStatus.video?.progress > 0 && taskStatus.video?.progress < 100) {
            setAlert({ severity: "info", message: "A video generation is currently in progress." });
          }
          state.setVideoProjectId(videoId);
          videoHandlers.handleOpenAbstractVideoDialog(state.setOpenAbstractVideoDialog);
        }}
        onViewPoster={async (posterId) => {
          setOpenPreviousAssets(false);
          setAlert({ severity: "info", message: "Loading poster..." });
          try {
            const url = await fetchPoster(posterId, token);
            if (url && typeof url === "string" && !url.error) {
              state.setPosterUrl(url);
              state.setPosterDownloadId(posterId);
              setAlert(null);
              state.setOpenPosterViewDialog(true);
            } else {
              setAlert({ severity: "error", message: url?.error || "Failed to load poster file." });
            }
          } catch (err) {
            setAlert({ severity: "error", message: "Error loading poster." });
          }
        }}
        onRegeneratePoster={(posterId) => {
          setOpenPreviousAssets(false);
          state.setPosterUrl("");
          setTaskStatus(prev => {
            const next = { ...prev };
            if (next.poster) {
              next.poster = { ...next.poster };
              delete next.poster[posterId];
            }
            return next;
          });
          posterHandlers.handleStartGeneratingPoster({
            publicationId,
            token,
            posterContentLevel: fetchedPosterGroup?.professional_type,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setOpenPosterDialog: state.setOpenPosterDialog,
            posterId
          });
        }}
        onViewAudio={async (audioId) => {
          setOpenPreviousAssets(false);
          setAlert({ severity: "info", message: "Loading audio..." });
          try {
            const url = await fetchAudio(audioId, token);
            if (url && typeof url === "string" && !url.error) {
              state.setAudioSummaryUrl(url);
              state.setAudioDownloadId(audioId);
              setAlert(null);
              state.setOpenAudioViewDialog(true);
            } else {
              setAlert({ severity: "error", message: url?.error || "Failed to load audio file." });
            }
          } catch (err) {
            setAlert({ severity: "error", message: "Error loading audio." });
          }
        }}
        onRegenerateAudio={(audioId) => {
          setOpenPreviousAssets(false);
          state.setAudioSummaryUrl("");
          setTaskStatus(prev => {
            const next = { ...prev };
            if (next.audio) {
              next.audio = { ...next.audio };
              delete next.audio[audioId];
            }
            return next;
          });
          audioHandlers.handleStartGeneratingAudio({
            publicationId,
            token,
            audioLevel: fetchedAudioGroup?.professional_type,
            audioDuration: null,
            audioVoice: null,
            setActiveTasks,
            setTaskStatus,
            setAlert,
            setupWebSocket,
            setOpenAudioSummaryDialog: state.setOpenAudioSummaryDialog,
            audioId
          });
        }}
      />

      {/* Navigation Blocking Dialog */}
      <Dialog open={blocker.state === "blocked"} onClose={handleCancelNavigation}>
        <DialogTitle>
          <IconButton
            onClick={handleCancelNavigation}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          Leave Page?
        </DialogTitle>
        <DialogContent>
          <p>
            Don't worry. Your asset generation is still in progress and will continue in the background. You will be notified once it is complete.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>Stay</Button>
          <Button onClick={handleConfirmNavigation} variant="contained" color="primary">
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssetsGeneration;
